import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { S, SERIF, SANS, IMG, Eyebrow, Reveal, GoldButton, Section, Grain } from "./slUi";
import { useSpeak, Portrait, SpeakButton } from "./slSpeak";

export const Hero = ({ g, onFounding }) => {
  const nav = useNavigate();
  const s = useSpeak("opening");
  const live = s.state === "playing" || s.state === "paused";
  return (
    <section className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden" style={{ background: S.night }} data-testid="sl-hero">
      <motion.img src={IMG.hero} alt="" initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 14, ease: "easeOut" }} className="absolute inset-0 w-full h-full object-cover object-[45%_40%]" fetchpriority="high" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(7,13,29,0.4) 0%, rgba(7,13,29,0) 25%, rgba(7,13,29,0.35) 60%, #070D1D 100%), linear-gradient(90deg, rgba(7,13,29,0.78) 0%, rgba(7,13,29,0.3) 45%, rgba(7,13,29,0) 75%)" }} />
      <Grain />
      <header className="absolute top-0 inset-x-0 px-6 sm:px-10 lg:px-20 pt-6 flex items-center justify-between" style={{ color: S.cream }}>
        <a href="/soulumo" className="text-[0.72rem] tracking-[0.5em]" style={{ fontFamily: SANS }} data-testid="sl-brand">SOULUMO</a>
        <button type="button" onClick={onFounding} data-testid="sl-header-founding" className="text-[0.6rem] uppercase tracking-[0.3em] transition-opacity hover:opacity-70" style={{ fontFamily: SANS, color: S.dim }}>Founding members</button>
      </header>
      <div className="relative px-6 sm:px-10 lg:px-20 pb-14 sm:pb-20 max-w-3xl pt-28">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.2 }} className="flex items-center gap-4">
          <Portrait size={76} speaking={s.state === "playing"} />
          <div>
            <p className="text-2xl" style={{ fontFamily: SERIF, color: S.cream }}>Adelaida</p>
            <p className="text-[0.66rem] uppercase tracking-[0.32em]" style={{ fontFamily: SANS, color: "#F0CFA9" }}>Your Soulumo guide</p>
          </div>
        </motion.div>
        <div className="mt-7 space-y-3" data-testid="sl-hero-lines">
          {g.opening_lines.map((l, i) => (
            <motion.p key={l} initial={{ opacity: 0, y: 10 }} animate={{ opacity: live ? (i < s.shown ? 1 : 0.2) : 1, y: 0 }} transition={{ duration: 0.7, delay: live ? 0 : 0.4 + i * 0.18 }}
              className={`${i === 0 ? "text-4xl sm:text-6xl" : "text-xl sm:text-2xl"} font-light leading-tight`} style={{ fontFamily: SERIF, color: i === 2 ? "#F0CFA9" : S.cream }}>{l}</motion.p>
          ))}
        </div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="mt-5 max-w-xl text-sm sm:text-base leading-relaxed" style={{ fontFamily: SANS, color: S.dim }}>
          Create something personal. Listen for a few minutes during the day. Take it into the night. Follow it for 21 days, or invite the people you care about to experience it with you.
        </motion.p>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="mt-9 flex flex-col sm:flex-row gap-3">
          <SpeakButton s={s} label="Meet Adelaida" testId="sl-hero-meet" />
          <GoldButton onClick={() => nav("/soulumo/demo?from=launch")} testId="sl-hero-experience">Experience Soulumo <ArrowRight size={14} /></GoldButton>
        </motion.div>
        <p className="mt-8 text-[0.62rem] uppercase tracking-[0.34em]" style={{ fontFamily: SANS, color: S.dim }}>Soulumo · Your inner world, in your own voice</p>
      </div>
    </section>
  );
};

const SHAPES = ["How you feel.", "How you see yourself.", "What you expect.", "How you respond.", "What you believe is possible."];

export const Problem = ({ g }) => {
  const s = useSpeak("problem");
  return (
    <Section tone="dark" testId="sl-problem" className="py-28 sm:py-40 overflow-hidden">
      <div className="relative max-w-3xl mx-auto">
        <Reveal><Eyebrow light>Why Soulumo exists</Eyebrow></Reveal>
        <Reveal delay={0.05}><h2 className="mt-5 text-4xl sm:text-6xl font-light leading-[1.05]" style={{ fontFamily: SERIF }}>Your inner dialogue is with you <span className="" style={{ color: S.gold }}>all day.</span></h2></Reveal>
        <div className="mt-12 space-y-2" data-testid="sl-problem-shapes">
          <Reveal><p className="text-sm uppercase tracking-[0.28em]" style={{ fontFamily: SANS, color: S.dim }}>It influences</p></Reveal>
          {SHAPES.map((l, i) => <Reveal key={l} delay={0.12 * i} y={14}><p className="text-2xl sm:text-4xl font-light" style={{ fontFamily: SERIF, color: i === SHAPES.length - 1 ? S.gold : S.cream }}>{l}</p></Reveal>)}
        </div>
        <Reveal delay={0.2}><p className="mt-12 max-w-xl text-base sm:text-lg leading-relaxed" style={{ fontFamily: SANS, color: S.dim }}>
          Soulumo gives you a simple way to begin changing that conversation, using your own words, your own voice and carefully created audio experiences you can return to every day.
        </p></Reveal>
        <Reveal delay={0.25}><p className="mt-8 text-2xl sm:text-3xl font-light" style={{ fontFamily: SERIF }}>Not just something to listen to.</p>
          <p className="text-2xl sm:text-3xl " style={{ fontFamily: SERIF, color: S.gold }}>Something created around you.</p></Reveal>
        <Reveal delay={0.3} className="mt-10"><SpeakButton s={s} label="Hear Adelaida" testId="sl-problem-play" /></Reveal>
        <p className="sr-only">{g.problem_lines.join(" ")}</p>
      </div>
    </Section>
  );
};

export const Cue = ({ text, tone = "light", testId }) => (
  <Section tone={tone} testId={testId} className="py-10 sm:py-14">
    <Reveal className="max-w-3xl mx-auto flex items-center gap-4">
      <Portrait size={52} />
      <p className="text-xl sm:text-2xl leading-snug" style={{ fontFamily: SERIF }}>{text}</p>
    </Reveal>
  </Section>
);
